import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import claimSaveApi from 'models/claims/claim/save/Api.Claim.Save';
import ClaimType from 'models/claims/claimType/Model.ClaimType';
import PayeeType from 'models/claims/payeeType/Model.PayeeType';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import importLegacyContractApi from 'models/sales/contract/legacy/importLegacyContract/Api.ImportLegacyContract';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { claimIndexRoute } from '../views/claim/Claim.Index';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import Payee from 'models/claims/payee/Model.Payee';
import { mapLegacyContractStatusForImport } from 'module/claims/helper/Claim.ContractEligibility';

export type ClaimCreation = {
  claimCreationOption: Model.Enum.ClaimCreatorOptions;
  dealerId?: number;
  repairFacilityId?: number;
};
export default function useClaimCreator() {
  const { user, userId, permissions } = useAuthStore();
  const [isCreatingClaim, setIsCreatingClaim] = useState<boolean>(false);
  const _history = useHistory();

  const isClaimAdmin = permissions?.CLAIM_ADMIN;
  const canCreateClaim = isClaimAdmin || permissions?.CREATE_CLAIM;
  const canCreateClaimForExpiredContract = isClaimAdmin || permissions?.CREATE_CLAIM_FOR_EXPIRED_CONTRACT;
  const canCreateSimpleClaim = isClaimAdmin || permissions?.CREATE_SIMPLE_CLAIM;

  const repairFacilityListApi = useApi({
    action: Payee.list,
    default: { payeeTypeId: [PayeeType.REPAIR_FACILITY_ID], orderBy: 'name', orderType: 'asc' },
  });
  const dealerListApi = useApi({
    action: Dealer.list,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? undefined,
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      orderBy: 'name',
      orderType: 'asc',
    },
  });

  const claimTypesApi = useApi({
    action: ClaimType.list,
    default: {
      dealerId: dealerListApi?.payload?.data?.map((it) => it.id) ?? [],
    },
  });
  const claimTypes = claimTypesApi.payload?.data;

  async function createClaim({
    contract,
    claimCreation,
    isForExpired,
  }: {
    contract: Model.IContract;
    claimCreation: ClaimCreation;
    isForExpired?: boolean;
  }) {
    setIsCreatingClaim(true);
    const claimTypeWithReport = claimTypes?.find((claimType) =>
      claimType.products?.some((product) => product.id === (contract?.product?.id || contract?.productId))
    );
    const res = await claimSaveApi({
      statusCode:
        claimCreation.claimCreationOption === ClaimCreatorOptions.DEALERSHIP
          ? ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP
          : claimCreation.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY
            ? ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY
            : ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER,
      submitedDateUtc: date().tz('UTC').format(defaultServerDateTimeFormat),
      dealerId: claimCreation?.dealerId,
      reporterId: userId,
      ownerId: null,
      contractId: contract.id,
      incidentLocalDate: isForExpired ? dayjs().format(defaultServerDateTimeFormat) : undefined,
      createdByPayee: {
        name: user?.fullName,
        email: user?.email,
        phoneNumber: undefined,
        payeeTypeId:
          claimCreation.claimCreationOption === ClaimCreatorOptions.DEALERSHIP
            ? PayeeType.DEALER_ID
            : claimCreation.claimCreationOption === ClaimCreatorOptions.REPAIR_FACILITY
              ? PayeeType.REPAIR_FACILITY_ID
              : undefined,
      },
      contract: { id: contract.id },
      claimJobs: !claimTypeWithReport?.claimTypeReportSettings?.length
        ? []
        : [
            {
              jobNumber: 1,
              claimType: { id: claimTypeWithReport.id },
            },
          ],
      contactName: contract?.client?.name,
      contactEmail: contract.client?.email,
      contactPhone: contract.client?.phone,
      streetAddress: contract.client?.streetAddress,
      city: contract.client?.city,
      postalCode: contract.client?.postalCode,
      countryCode: contract.client?.countryCode,
      provinceCode: contract.client?.provinceCode,
    });

    if (res.status !== HttpStatus.ok) {
      addLog({ error: res.message });
      setIsCreatingClaim(false);
    } else {
      if (res.payload) {
        setIsCreatingClaim(false);
        _history.push(createPathFromRoute(claimIndexRoute, {}));
      }
    }
  }
  const createClaimWithLegacyContract = async ({
    contract,
    claimCreation,
    isForExpired,
  }: {
    contract: Model.IContract;
    claimCreation: ClaimCreation;
    isForExpired?: boolean;
  }) => {
    setIsCreatingClaim(true);
    if (!contract.id) {
      const contractForImport = mapLegacyContractStatusForImport(contract);
      const res = await importLegacyContractApi(contractForImport);

      if (!res.payload?.id) {
        addLog({ error: res.message });
        setIsCreatingClaim(false);
        return;
      }

      contract = res.payload;
    }
    createClaim({ contract, isForExpired, claimCreation });
  };

  return {
    canCreateClaim,
    canCreateSimpleClaim,
    canCreateClaimForExpiredContract,
    createClaim,
    createClaimWithLegacyContract,
    isCreatingClaim,
    setIsCreatingClaim,
    dealerListApi,
    repairFacilityListApi,
  };
}
